// src/layouts/agente/index.jsx

import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import Footer from "examples/Footer";

// ✅ Define o caminho base da API dinamicamente para dev e prod
const apiBaseURL =
  window.location.hostname === "localhost"
    ? "http://localhost/api/gpt"
    : "https://www.perfectoespanhol.com.br/api/gpt";

function detectarPerfil(texto) {
  const lower = texto.toLowerCase();

  if (
    lower.includes("funcionário") ||
    lower.includes("recrutamento") ||
    lower.includes("colaborador") ||
    lower.includes("equipe")
  )
    return "rh";

  if (
    lower.includes("marca") ||
    lower.includes("design") ||
    lower.includes("identidade visual") ||
    lower.includes("layout")
  )
    return "criativo";

  if (
    lower.includes("negócio") ||
    lower.includes("investimento") ||
    lower.includes("abrir") ||
    lower.includes("começar") ||
    lower.includes("venda")
  )
    return "empreendedor";

  return "empresarial";
}

const promptPorPerfil = {
  rh: `
    Você representa Alexandre Fichtler, especialista em design digital e gráfico, UX, UI, automações e soluções digitais para empresas. Sua missão é entender as necessidades relacionadas à gestão de pessoas e propor soluções inteligentes com base no portfólio apresentado. Mesmo que algum item não seja realizado diretamente, Alexandre orienta pessoalmente ou indica um parceiro confiável. Use um tom estratégico e empático. Contato: WhatsApp +55 11 98955-0168 ou suporte@perfectoespanhol.com.
  `,

  criativo: `
    Você representa Alexandre Fichtler, designer visual especializado em design digital e gráfico, com foco em experiências de UX/UI modernas e personalizadas. Sua abordagem deve ser criativa, alinhada à comunicação visual e digital do cliente. Não exclua nenhum serviço: todos do portfólio podem ser realizados ou direcionados por Alexandre. Contato: WhatsApp +55 11 98955-0168 ou suporte@perfectoespanhol.com.
  `,

  empreendedor: `
    Você representa Alexandre Fichtler, especialista em soluções práticas e acessíveis para novos negócios, com foco em design digital e gráfico, UX/UI e automações. Foque em soluções diretas, de excelente custo-benefício, com linguagem simples e eficaz. Alexandre sempre pode orientar ou encaminhar o projeto, mesmo que o serviço não seja feito internamente. Contato: WhatsApp +55 11 98955-0168 ou suporte@perfectoespanhol.com.
  `,

  empresarial: `
    Você representa Alexandre Fichtler, especialista em design digital e gráfico, UX, UI e automações empresariais. Use tom confiável e objetivo. Todos os serviços do portfólio estão disponíveis ou podem ser encaminhados diretamente por Alexandre. Nunca diga que algo não pode ser feito. Contato: WhatsApp +55 11 98955-0168 ou suporte@perfectoespanhol.com.
  `
};

function SolucaoInteligente() {
  const [mensagem, setMensagem] = useState("");
  const [conversa, setConversa] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery("(max-width:389px)");
  const location = useLocation();

  const jaEnviadoRef = useRef(false);      // Evita múltiplos envios
  const conversaRef = useRef([]);          // Mantém conversa atualizada

  const enviarMensagem = async () => {
    if (!mensagem.trim()) return;

    const perfilDetectado = detectarPerfil(mensagem);
    const promptSystem = {
      role: "system",
      content: promptPorPerfil[perfilDetectado],
    };

    const novaConversa =
      conversa.length === 0
        ? [promptSystem, { role: "user", content: mensagem }]
        : [...conversa, { role: "user", content: mensagem }];

    setConversa(novaConversa);
    conversaRef.current = novaConversa;

    setMensagem("");
    setLoading(true);

    try {
      const response = await fetch(`${apiBaseURL}/consultoria.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: novaConversa }),
      });

      const data = await response.json();
      const respostaIA = data.resposta || "Não foi possível gerar resposta.";

      const conversaAtualizada = [...novaConversa, { role: "assistant", content: respostaIA }];
      setConversa(conversaAtualizada);
      conversaRef.current = conversaAtualizada;
    } catch (error) {
      console.error("Erro na requisição:", error);
    }

    setLoading(false);
  };

  // Scroll automático
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversa]);

  // Garante que conversaRef esteja sempre atualizada
  useEffect(() => {
    conversaRef.current = conversa;
  }, [conversa]);

  // Função separada de envio do histórico
  const enviarConversaFinal = () => {
    if (!jaEnviadoRef.current && conversaRef.current.length > 1) {
      jaEnviadoRef.current = true;
      navigator.sendBeacon(
        `${apiBaseURL}/finalizar.php`,
        JSON.stringify({ historico: conversaRef.current })
      );
    }
  };

  // Envia ao sair do navegador
  useEffect(() => {
    const handleBeforeUnload = () => {
      enviarConversaFinal();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Envia ao sair da rota (com delay)
useEffect(() => {
  return () => {
    setTimeout(() => {
      enviarConversaFinal();
    }, 300);
  };
}, [location.pathname]); // ← CORRETO  

  return (
    <DashboardLayout>
      <DefaultNavbar />
      <VuiBox py={3} display="flex" justifyContent="center">
        <Card sx={{ maxWidth: "1000px", width: "100%" }}>
          <CardContent>
            <VuiBox px={2}>
              <VuiTypography variant="button" color="success" fontWeight="bold" mb={1}>
                Consultoria Inteligente com IA
              </VuiTypography>

              <VuiTypography variant="h2" color="white" fontWeight="bold" mb={2}>
                Descreva seu desafio ou projeto
              </VuiTypography>

              <VuiTypography variant="body2" color="text" mb={3}>
                Digite no campo abaixo uma descrição do seu projeto, ideia ou necessidade. A IA analisará e retornará
                sugestões personalizadas com base nas suas informações.
              </VuiTypography>

              {conversa.slice(1).some((msg) => msg.content?.trim()) && (
                <VuiBox
                  width="100%"
                  mb={3}
                  p={2}
                  border="1px solid #444"
                  borderRadius="lg"
                  sx={{ backgroundColor: "#1E293B", maxHeight: "400px", overflowY: "auto" }}
                >
                  {conversa.slice(1).map((msg, i) =>
                    msg.content?.trim() ? (
                      <VuiTypography
                        key={i}
                        variant="body2"
                        color={msg.role === "user" ? "info" : "text"}
                        mb={2}
                        textAlign={msg.role === "user" ? "right" : "left"}
                        component="div"
                        dangerouslySetInnerHTML={{
                          __html: `<strong>${msg.role === "user" ? "Você:" : "IA:"}</strong> ` +
                            msg.content
                              .replace(/\n\n/g, "<br /><br />")            // parágrafos
                              .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // negrito
                        }}
                      />
                    ) : null
                  )}
                  <VuiBox ref={messagesEndRef} sx={{ height: 0, overflow: "hidden", m: 0, p: 0 }} />
                </VuiBox>
              )}

              <textarea
                rows="6"
                placeholder="Ex: Preciso de um site moderno para divulgar meus serviços médicos..."
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                style={{
                  width: "100%",
                  fontSize: "1rem",
                  padding: "14px",
                  borderRadius: "10px",
                  backgroundColor: "#1E293B",
                  border: "1px solid #4B5563",
                  resize: "vertical",
                  boxSizing: "border-box",
                  color: "#fff",
                  marginBottom: "16px",
                }}
              />

              <VuiButton
                onClick={enviarMensagem}
                variant="contained"
                color="success"
                sx={{ mt: 3 }}
                disabled={loading}
              >
                {loading ? "Analisando..." : "ENVIAR"}
              </VuiButton>
            </VuiBox>
          </CardContent>
        </Card>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SolucaoInteligente;